import React, { useState, useEffect } from 'react';
import { SmartDevice, MorningRoutine, RoutineStep, RoutineExecutionState } from './interfaces/types';
import { Sun, Sunrise, Coffee, Moon, Droplet, Zap, CheckCircle } from 'lucide-react';

interface DemoVisualizerProps {
  routines: MorningRoutine[];
  devices: SmartDevice[];
}

const DemoVisualizer: React.FC<DemoVisualizerProps> = ({ routines, devices }) => {
  const [demoMode, setDemoMode] = useState<boolean>(false);
  const [demoSpeed, setDemoSpeed] = useState<number>(1);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [activeRoutine, setActiveRoutine] = useState<MorningRoutine | null>(null);
  const [executionState, setExecutionState] = useState<RoutineExecutionState | null>(null);
  const [deviceStates, setDeviceStates] = useState<Map<string, boolean>>(new Map());
  const [demoStep, setDemoStep] = useState<number>(0);
  const [demoProgress, setDemoProgress] = useState<number>(0);
  
  // Initialize device states
  useEffect(() => {
    const initialStates = new Map<string, boolean>();
    devices.forEach(device => {
      initialStates.set(device.id, false);
    });
    setDeviceStates(initialStates);
  }, [devices]);
  
  // Reset demo
  const resetDemo = () => {
    setDemoMode(false);
    setCurrentTime(new Date());
    setDemoStep(0);
    setDemoProgress(0);
    setExecutionState(null);
    
    // Reset all devices to off
    const resetStates = new Map<string, boolean>();
    devices.forEach(device => {
      resetStates.set(device.id, false);
    });
    setDeviceStates(resetStates);
  };
  
  // Start demo
  const startDemo = () => {
    // Find the first enabled routine
    const routine = routines.find(r => r.enabled);
    if (!routine) return;
    
    setActiveRoutine(routine);
    setDemoMode(true);
    
    // Set initial execution state
    const firstStep = routine.steps[0];
    if (firstStep) {
      setExecutionState({
        routineId: routine.id,
        startTime: new Date(),
        currentStepId: firstStep.id,
        completedStepIds: [],
        skippedStepIds: [],
        failedStepIds: [],
        status: 'IN_PROGRESS'
      });
    }
    
    // Reset time to 6 AM
    const demoStartTime = new Date();
    demoStartTime.setHours(6, 0, 0, 0);
    setCurrentTime(demoStartTime);
  };
  
  // Advance demo time
  useEffect(() => {
    if (!demoMode || !activeRoutine) return;
    
    const timeInterval = setInterval(() => {
      setCurrentTime(prev => {
        const newTime = new Date(prev);
        newTime.setMinutes(newTime.getMinutes() + 5 * demoSpeed);
        return newTime;
      });
      
      // Advance demo progress
      setDemoProgress(prev => {
        const newProgress = prev + 1 * demoSpeed;
        if (newProgress >= 100) {
          // Demo complete
          clearInterval(timeInterval);
          setExecutionState(prev => prev ? { ...prev, status: 'COMPLETED' } : null);
          return 100;
        }
        return newProgress;
      });
    }, 1000); // Update every second
    
    return () => clearInterval(timeInterval);
  }, [demoMode, activeRoutine, demoSpeed]);
  
  // Process routine steps based on demo progress
  useEffect(() => {
    if (!demoMode || !activeRoutine || !executionState) return;
    
    // Map demo progress (0-100) to steps in the routine
    const totalSteps = activeRoutine.steps.length;
    const progressPerStep = 100 / totalSteps;
    const currentStepIndex = Math.min(Math.floor(demoProgress / progressPerStep), totalSteps - 1);
    
    // Only update if we've moved to a new step
    if (currentStepIndex !== demoStep) {
      setDemoStep(currentStepIndex);
      
      // Get the current step
      const step = activeRoutine.steps[currentStepIndex];
      
      // Update execution state
      if (step) {
        setExecutionState(prev => {
          if (!prev) return null;
          
          // Mark previous steps as completed
          const newCompletedSteps = [...prev.completedStepIds];
          
          // Add all steps up to current step (except current) to completed list
          for (let i = 0; i < currentStepIndex; i++) {
            const stepId = activeRoutine.steps[i].id;
            if (!newCompletedSteps.includes(stepId)) {
              newCompletedSteps.push(stepId);
            }
          }
          
          return {
            ...prev,
            currentStepId: step.id,
            completedStepIds: newCompletedSteps
          };
        });
        
        // Execute the actions for this step
        executeStepActions(step);
      }
    }
  }, [demoProgress, demoMode, activeRoutine, executionState, demoStep]);
  
  // Execute the actions for a step
  const executeStepActions = (step: RoutineStep) => {
    step.actions.forEach(action => {
      // Update device state based on action
      setDeviceStates(prev => {
        const newStates = new Map(prev);
        
        switch (action.command.commandType) {
          case 'ON':
            newStates.set(action.deviceId, true);
            break;
          case 'OFF':
            newStates.set(action.deviceId, false);
            break;
          default:
            // For other command types like DIM, OPEN, etc., we'll just set the device to on
            newStates.set(action.deviceId, true);
            break;
        }
        
        return newStates;
      });
    });
  };
  
  // Format time for display
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Get device name by ID
  const getDeviceName = (deviceId: string) => {
    const device = devices.find(d => d.id === deviceId);
    return device ? device.name : deviceId;
  };
  
  // Get device icon
  const getDeviceIcon = (deviceId: string) => {
    const device = devices.find(d => d.id === deviceId);
    if (!device) return <Zap />;
    
    switch (device.type) {
      case 'LIGHT':
        return <Sun className="text-yellow-500" />;
      case 'COFFEE_MACHINE':
        return <Coffee className="text-brown-500" />;
      case 'BLIND':
        return <Sunrise className="text-blue-500" />;
      default:
        return <Zap className="text-purple-500" />;
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6 mt-4">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold">Morning Routine Demo</h3>
        
        <div className="flex space-x-3">
          {!demoMode ? (
            <button 
              onClick={startDemo}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Start Demo
            </button>
          ) : (
            <button 
              onClick={resetDemo}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Reset Demo
            </button>
          )}
          
          {demoMode && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Speed:</span>
              <select 
                value={demoSpeed}
                onChange={e => setDemoSpeed(Number(e.target.value))}
                className="border rounded p-1"
              >
                <option value="0.5">0.5x</option>
                <option value="1">1x</option>
                <option value="2">2x</option>
                <option value="5">5x</option>
              </select>
            </div>
          )}
        </div>
      </div>
      
      {/* Demo visualization area */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left column - Routine info and progress */}
        <div className="col-span-12 md:col-span-5 space-y-4">
          {/* Current time */}
          <div className="bg-gray-100 p-4 rounded-lg">
            <div className="text-sm text-gray-500 mb-1">Current Time</div>
            <div className="flex items-center">
              <Clock className="mr-2 text-blue-500" size={24} />
              <span className="text-2xl font-bold">{formatTime(currentTime)}</span>
            </div>
          </div>
          
          {/* Active routine */}
          {activeRoutine && (
            <div className="bg-gray-100 p-4 rounded-lg">
              <div className="text-sm text-gray-500 mb-1">Active Routine</div>
              <div className="font-bold text-lg mb-2">{activeRoutine.name}</div>
              <div className="text-sm text-gray-600 mb-4">{activeRoutine.description}</div>
              
              {/* Progress bar */}
              <div className="mb-2 flex justify-between items-center">
                <span className="text-sm font-medium">Progress</span>
                <span className="text-sm text-gray-500">{demoProgress.toFixed(0)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                  style={{ width: `${demoProgress}%` }}
                ></div>
              </div>
              
              {/* Steps list */}
              <div className="mt-4">
                <div className="text-sm font-medium mb-2">Steps</div>
                <div className="space-y-2">
                  {activeRoutine.steps.map((step, index) => {
                    // Determine step status
                    let status = 'pending';
                    if (executionState?.completedStepIds.includes(step.id)) {
                      status = 'completed';
                    } else if (executionState?.currentStepId === step.id) {
                      status = 'active';
                    } else if (executionState?.skippedStepIds.includes(step.id)) {
                      status = 'skipped';
                    } else if (executionState?.failedStepIds.includes(step.id)) {
                      status = 'failed';
                    }
                    
                    return (
                      <div 
                        key={step.id} 
                        className={`p-3 rounded-lg border ${
                          status === 'active' ? 'border-blue-300 bg-blue-50' :
                          status === 'completed' ? 'border-green-300 bg-green-50' :
                          status === 'failed' ? 'border-red-300 bg-red-50' :
                          status === 'skipped' ? 'border-yellow-300 bg-yellow-50' :
                          'border-gray-200'
                        }`}
                      >
                        <div className="flex justify-between">
                          <div className="font-medium">
                            {index + 1}. {step.name}
                          </div>
                          <div>
                            {status === 'completed' && <CheckCircle size={16} className="text-green-500" />}
                          </div>
                        </div>
                        
                        <div className="text-xs text-gray-500 mt-1">
                          {step.condition.type === 'TIME' && step.condition.timeWindow && (
                            <span>When: {step.condition.timeWindow.start.hour}:{step.condition.timeWindow.start.minute.toString().padStart(2, '0')} - {step.condition.timeWindow.end.hour}:{step.condition.timeWindow.end.minute.toString().padStart(2, '0')}</span>
                          )}
                          {step.condition.type === 'BED_EXIT' && (
                            <span>When: Bed exit detected</span>
                          )}
                          {step.condition.type === 'MOTION' && (
                            <span>When: Motion detected</span>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="mt-2 text-xs">
                          {step.actions.map((action, actionIndex) => (
                            <div key={actionIndex} className="flex items-center mt-1">
                              <div className="w-5 h-5 flex-shrink-0 mr-1">
                                {getDeviceIcon(action.deviceId)}
                              </div>
                              <span className="mr-1">{getDeviceName(action.deviceId)}:</span>
                              <span className={`font-medium ${
                                action.command.commandType === 'ON' ? 'text-green-600' :
                                action.command.commandType === 'OFF' ? 'text-red-600' :
                                'text-blue-600'
                              }`}>
                                {action.command.commandType}
                                {action.command.value !== undefined && ` (${action.command.value})`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Right column - Smart home visualization */}
        <div className="col-span-12 md:col-span-7">
          <div className="bg-gray-100 p-6 rounded-lg h-full">
            <h3 className="text-lg font-medium mb-4">Smart Home Visualization</h3>
            
            {/* Home layout visualization */}
            <div className="relative w-full" style={{ height: '400px', backgroundColor: '#f0f4f8', border: '2px solid #cbd5e0', borderRadius: '8px' }}>
              {/* Bedroom */}
              <div className="absolute inset-0 m-4 bg-white rounded shadow-md flex flex-col">
                <div className="p-3 border-b border-gray-200 bg-blue-50">
                  <div className="font-medium">Bedroom</div>
                </div>
                <div className="p-3 flex-grow relative">
                  {/* Bed */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-20 bg-blue-200 rounded-lg border border-blue-300 flex items-center justify-center">
                    <Moon size={20} className="text-blue-700" />
                    <span className="ml-1 text-sm">Bed</span>
                  </div>
                  
                  {/* Lights */}
                  <div 
                    className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center ${
                      deviceStates.get('light-bedroom') ? 'bg-yellow-300' : 'bg-gray-300'
                    }`}
                  >
                    <Sun size={16} className={deviceStates.get('light-bedroom') ? 'text-yellow-600' : 'text-gray-600'} />
                  </div>
                  
                  {/* Blinds */}
                  <div className="absolute h-8 top-2 left-2 w-16 rounded-sm flex items-center justify-center">
                    <div className={`absolute inset-0 ${
                      deviceStates.get('blinds-bedroom') ? 'bg-blue-400' : 'bg-gray-400'
                    }`}></div>
                    <Sunrise size={16} className="text-white z-10" />
                  </div>
                </div>
              </div>
              
              {/* Kitchen (top right) */}
              <div className="absolute top-4 right-4 w-1/3 h-40 bg-white rounded shadow-md flex flex-col">
                <div className="p-2 border-b border-gray-200 bg-green-50">
                  <div className="font-medium text-sm">Kitchen</div>
                </div>
                <div className="p-2 flex-grow flex items-center justify-center">
                  {/* Coffee machine */}
                  <div className={`w-12 h-12 rounded-md flex items-center justify-center ${
                    deviceStates.get('coffee-kitchen') ? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                    <Coffee 
                      size={24} 
                      className={deviceStates.get('coffee-kitchen') ? 'text-red-600' : 'text-gray-400'} 
                    />
                  </div>
                </div>
              </div>
              
              {/* Bathroom (lower right) */}
              <div className="absolute bottom-4 right-4 w-1/3 h-40 bg-white rounded shadow-md flex flex-col">
                <div className="p-2 border-b border-gray-200 bg-cyan-50">
                  <div className="font-medium text-sm">Bathroom</div>
                </div>
                <div className="p-2 flex-grow relative">
                  {/* Bathroom light */}
                  <div 
                    className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center ${
                      deviceStates.get('light-bathroom') ? 'bg-yellow-300' : 'bg-gray-300'
                    }`}
                  >
                    <Sun size={12} className={deviceStates.get('light-bathroom') ? 'text-yellow-600' : 'text-gray-600'} />
                  </div>
                  
                  {/* Shower icon */}
                  <div className="absolute bottom-2 left-2 w-8 h-8 bg-blue-100 rounded-sm flex items-center justify-center">
                    <Droplet size={16} className="text-blue-600" />
                  </div>
                </div>
              </div>
              
              {/* Hallway */}
              <div className="absolute top-1/2 right-1/3 transform -translate-y-1/2 w-16 h-40 bg-white rounded shadow-md flex items-center justify-center">
                <div className="p-2 text-center">
                  <div className="font-medium text-xs mb-1">Hallway</div>
                  {/* Hallway light */}
                  <div 
                    className={`mx-auto w-6 h-6 rounded-full flex items-center justify-center ${
                      deviceStates.get('light-hallway') ? 'bg-yellow-300' : 'bg-gray-300'
                    }`}
                  >
                    <Sun size={12} className={deviceStates.get('light-hallway') ? 'text-yellow-600' : 'text-gray-600'} />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Device status legend */}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              {Array.from(deviceStates.entries()).map(([deviceId, isOn]) => (
                <div key={deviceId} className="flex items-center space-x-2 border rounded-lg p-2">
                  <div className={`w-3 h-3 rounded-full ${isOn ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <div className="flex items-center">
                    <div className="w-5 h-5 mr-1">
                      {getDeviceIcon(deviceId)}
                    </div>
                    <span className="text-sm">{getDeviceName(deviceId)}</span>
                  </div>
                  <span className={`text-xs font-medium ml-auto ${isOn ? 'text-green-600' : 'text-gray-500'}`}>
                    {isOn ? 'ON' : 'OFF'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Quick explanation */}
      {!demoMode && (
        <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2">How to Use the Demo</h4>
          <p className="text-sm text-blue-700">
            Click "Start Demo" to visualize how a morning routine would execute in a real home. 
            The demo will simulate a typical morning scenario, activating smart devices based on
            conditions like time and bed exit detection. Adjust the speed to see the full sequence faster.
          </p>
        </div>
      )}
    </div>
  );
};

// Clock component for the demo
const Clock: React.FC<{ className?: string; size?: number }> = ({ className, size = 24 }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

export default DemoVisualizer; 